// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { AppIcon } from './AppIcon';
import './AlertInfoPanel.css';

const VARIANT_CONFIG: Record<
  string,
  { icon: string; bgClass: string; colorClass: string }
> = {
  error: {
    icon: 'warning sign',
    bgClass: 'fin-alert-info-panel--error',
    colorClass: 'fin-alert-info-panel__icon-circle--error',
  },
  warning: {
    icon: 'warning sign',
    bgClass: 'fin-alert-info-panel--warning',
    colorClass: 'fin-alert-info-panel__icon-circle--warning',
  },
  info: {
    icon: 'info circle',
    bgClass: 'fin-alert-info-panel--info',
    colorClass: 'fin-alert-info-panel__icon-circle--info',
  },
};

export interface AlertInfoField {
  /** Field label */
  label: string;
  /** Field value (displayed read-only) */
  value?: string;
  /** Whether to render as multiline (textarea-style) vs single-line */
  multiline?: boolean;
  /** Whether to show the required asterisk on the label */
  required?: boolean;
}

export interface AlertInfoPanelProps {
  /** Panel variant controls icon and icon-circle color */
  variant: 'error' | 'warning' | 'info';
  /** Panel title displayed next to the icon */
  title: string;
  /** List of read-only fields to display */
  fields: AlertInfoField[];
  /** Optional extra CSS class */
  className?: string;
}

export function AlertInfoPanel({
  variant,
  title,
  fields,
  className = '',
}: Readonly<AlertInfoPanelProps>) {
  const config = VARIANT_CONFIG[variant];

  const classes = [
    'fin-alert-info-panel',
    config.bgClass,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} role="alert">
      <div className="fin-alert-info-panel__header">
        <div
          className={`fin-alert-info-panel__icon-circle ${config.colorClass}`}
        >
          <AppIcon name={config.icon} size={20} />
        </div>
        <h3 className="fin-alert-info-panel__title">{title}</h3>
      </div>
      <div className="fin-alert-info-panel__body">
        {fields.map((field) => (
          <div key={field.label} className="fin-alert-info-panel__field">
            <div className="fin-alert-info-panel__field-label">
              {field.label}
              {field.required && (
                <span className="fin-alert-info-panel__field-required">*</span>
              )}
            </div>
            {field.value && (
              <div
                className={[
                  'fin-alert-info-panel__field-value',
                  field.multiline &&
                    'fin-alert-info-panel__field-value--multiline',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {field.value}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
